// import { is } from "date-fns/locale";

import { color } from "d3";
import { isNonCashTransaction } from '../../utils/categoryOptions';

/**
 * Transforms a list of JSON data into Sankey chart format with nodes and links,
 * performing only the aggregation step without swapping or cycle detection
 *
 * @param {Array} data - The input JSON data array
 * @param {Object} options - Configuration options
 * @returns {Object} Object with 'nodes' and 'links' arrays in Sankey format
 */
export function aggregateToSankeyData(data = [], options = {}) {
  // Default options
  const defaults = {
    // Node mappers
    getSourceNodeId: item => item.from || item.source,
    getSourceNodeName: item => item.fromName || item.sourceName || item.from || item.source,
    getTargetNodeId: item => item.to || item.target,
    getTargetNodeName: item => item.toName || item.targetName || item.to || item.target,
    getSourceTransactionID: item => item.source_txn_id || item.source_id,
    getTargetTransactionID: item => item.target_txn_id || item.target_id,
    getSourceAnchorLevel: item => item.source_anchor || 0,
    getTargetAnchorLevel: item => item.target_anchor || 0,
    getNodeValue: item => item.value || item.amount || 1,
    getNodeColor: null,

    // Link mappers
    getLinkValue: item => item.value || item.amount || 1,
    getLinkTransactionID: item => item.source_txn_id || item.source_id,
    getLinkColor: null,

    // List of categorical nodes (not used in output but kept for future extensions)
    categoricalNodes: []
  };

  // Merge provided options with defaults
  const config = { ...defaults, ...options };

  // Skip empty data
  if (!data || !Array.isArray(data) || data.length === 0) {
    return { nodes: [], links: [] };
  }

  // console.log(`Aggregating ${data.length} records into Sankey format...`);
  // console.log("Data:", data);

  // STEP 1: Collect all nodes
  const nodeMap = new Map();

  data.forEach(item => {
    const sourceId = config.getSourceNodeId(item);
    const targetId = config.getTargetNodeId(item);

    if (sourceId && !nodeMap.has(sourceId)) {
      nodeMap.set(sourceId, {
        id: sourceId,
        name: config.getSourceNodeName(item),
        anchorLevel: config.getSourceAnchorLevel(item),
        transactionIDs: new Set(),
        transactionValue: 0
      });
    }

    if (targetId && !nodeMap.has(targetId)) {
      nodeMap.set(targetId, {
        id: targetId,
        name: config.getTargetNodeName(item),
        anchorLevel: config.getTargetAnchorLevel(item),
        transactionIDs: new Set(),
        transactionValue: 0
      });
    }

    const sourceAnchorLevel = config.getSourceAnchorLevel(item) || 0;
      const targetAnchorLevel = config.getTargetAnchorLevel(item) || 0;


    // Add transaction ID to the source node
    if (sourceId) {
      const sourceNode = nodeMap.get(sourceId);
      //const targetNode = nodeMap.get(targetId);

      if (sourceId === "Income - Credit Card Payment" || targetId === "Income - Credit Card Payment") {
        console.log("Processing item:", item);
        console.log("sourceAnchorLevel:", sourceAnchorLevel);
        console.log("targetAnchorLevel:", targetAnchorLevel);
      }

      if (sourceNode) {
        const transactionID = config.getSourceTransactionID(item)
        if (transactionID && !sourceNode.transactionIDs.has(transactionID)) {
          sourceNode.transactionIDs.add(transactionID);
          // Update transaction value
          const value = config.getNodeValue(item);
          if (sourceAnchorLevel === 0 || targetAnchorLevel === 0) {
            // If source is not anchor or target is not anchor, count the value
            if (sourceId === "Income - Credit Card Payment" || targetId === "Income - Credit Card Payment") {
              console.log(`  In Source Node, Counting value for ${sourceId} (anchorLevel ${sourceAnchorLevel}) → ${targetId} (anchorLevel ${targetAnchorLevel}): $${value}`);
              console.log("  sourceNode before:", sourceNode);
            }
            sourceNode.transactionValue -= value;
            if (sourceId === "Income - Credit Card Payment" || targetId === "Income - Credit Card Payment") {
              console.log("  sourceNode after:", sourceNode);
            }
          }
          else if (sourceAnchorLevel !== 0 && targetAnchorLevel !== 0 && sourceAnchorLevel > targetAnchorLevel) {
            // Income type flows should always count
            if (sourceId === "Income - Credit Card Payment" || targetId === "Income - Credit Card Payment") {
              console.log(`  In Source Node, Counting value for ${sourceId} (anchorLevel ${sourceAnchorLevel}) → ${targetId} (anchorLevel ${targetAnchorLevel}): $${value}`);
              console.log("  sourceNode before:", sourceNode);
            }
            sourceNode.transactionValue += value;
            if (sourceId === "Income - Credit Card Payment" || targetId === "Income - Credit Card Payment") {
              console.log("  sourceNode after:", sourceNode);
            }
          }
        }
        if (!sourceNode.anchorLevel ) {
          sourceNode.anchorLevel = sourceAnchorLevel;
        }
      }
    }

    // Add transaction ID to the target node
    if (targetId) {
      const targetNode = nodeMap.get(targetId);
      if (targetNode) {
        const transactionID = config.getTargetTransactionID(item);
        if (transactionID && !targetNode.transactionIDs.has(transactionID)) {
          targetNode.transactionIDs.add(transactionID);
          // Update transaction value
          const value = config.getNodeValue(item);
          if (sourceId === "Income - Credit Card Payment" || targetId === "Income - Credit Card Payment") {
              console.log(`  In Target Node, Counting value for ${sourceId} (anchorLevel ${sourceAnchorLevel}) → ${targetId} (anchorLevel ${targetAnchorLevel}): $${value}`);
              console.log("  targetNode before:", targetNode);
            }
          targetNode.transactionValue += value;
          if (sourceId === "Income - Credit Card Payment" || targetId === "Income - Credit Card Payment") {
              console.log("  targetNode after:", targetNode);
            }
        }
        if (!targetNode.anchorLevel ) {
          targetNode.anchorLevel = config.getTargetAnchorLevel(item);
        }
      }
    }

  });

  // STEP 2: Aggregate flow values by source/target pair
  const flowMap = new Map();

  data.forEach(item => {
    const sourceId = config.getSourceNodeId(item);
    const targetId = config.getTargetNodeId(item);

    if (!sourceId || !targetId) {
      console.warn("Skipping item with missing source or target");
      return;
    }

    // Skip self-loops
    if (sourceId === targetId) {
      console.warn(`Skipping self-loop: ${sourceId} → ${sourceId}`);
      return;
    }

    // Get the value
    const value = config.getLinkValue(item);

    // get transaction ID for reference
    const transactionID = config.getLinkTransactionID(item);

    // Flow key combines source and target
    const flowKey = `${sourceId}|${targetId}`;

    // Add to aggregated flows or create new
    if (flowMap.has(flowKey)) {
      flowMap.get(flowKey).value += value;
      flowMap.get(flowKey).transactionIDs.add(transactionID);
    } else {
      flowMap.set(flowKey, {
        source: sourceId,
        target: targetId,
        value: value,
        transactionIDs: new Set([transactionID])
      });
    }
  });

  console.log(`Aggregated to ${nodeMap.size} nodes and ${flowMap.size} links.`);
  const sampleNode = nodeMap.get("Income - Credit Card Payment");
  if (sampleNode) {
    console.log("Income - Credit Card Payment Node:", sampleNode);
  }

  // STEP 3: Create final node array
  const nodes = Array.from(nodeMap.values()).map(node => {
    if (node.name === "Income - Credit Card Payment") {
      console.log("Final Income - Credit Card Payment Node:", node);
    }
    return {
      id: node.id,
      name: node.name,
      transactionIDs: node.transactionIDs,
      transactionValue: node.transactionValue,
      anchorLevel: node.anchorLevel
    };
  });
  // STEP 4: Create final links array
  const links = Array.from(flowMap.values()).map(flow => {
    return {
      source: flow.source,
      target: flow.target,
      value: flow.value,
      transactionIDs: flow.transactionIDs // Uncomment if you want to keep transaction IDs
    };
  });

  // console.log("Final Nodes:", nodes);
  // Return the aggregated data
  return { nodes, links };
}

/**
 * Processes a Sankey data structure and reverses any links with negative values.
 * For negative links, the source and target are swapped and the value is made positive.
 *
 * @param {Object} sankeyData - Object containing nodes and links arrays
 * @param {Array} sankeyData.nodes - Array of node objects
 * @param {Array} sankeyData.links - Array of link objects
 * @returns {Object} Processed Sankey data with the same structure
 */
export function reverseNegativeLinks(sankeyData) {
  // Handle empty or invalid input
  if (!sankeyData || !sankeyData.links || !Array.isArray(sankeyData.links)) {
    return sankeyData;
  }

  // Create a copy of the input data to avoid mutation
  const result = {
    nodes: sankeyData.nodes ? [...sankeyData.nodes] : [],
    links: []
  };

  // Process each link
  result.links = sankeyData.links.map(link => {
    // If the link value is negative, swap source and target and make value positive
    if (link.value < 0) {
      return {
        source: link.target,
        target: link.source,
        value: Math.abs(link.value),
        transactionIDs: link.transactionIDs || new Set(),
      };
    }

    // Otherwise, return the link unchanged
    return { ...link };
  });

  // Update the nodes to reflect the transaction values
  // result.nodes.forEach(node => {
  //   // Initialize transaction value
  //   node.transactionValue = 0;
  //   // console.log(`processing node ${node.id}`);
  //   // console.log("node.transactionValue", node.transactionValue);

  //   // Calculate the total transaction value for each node
  //   result.links.forEach(link => {
  //     // console.log("link", link);
  //     // console.log("link.source", link.source);
  //     // console.log("link.target", link.target);
  //     // console.log("link.value", link.value);
  //     if (link.source === node.id) {
  //       // console.log("subtracting link.value from node.transactionValue");
  //       node.transactionValue -= link.value;
  //     }
  //     if (link.target === node.id) {
  //       // console.log("adding link.value to node.transactionValue");
  //       node.transactionValue += link.value;
  //     }
  //   });
  //   node.transactionValue = Math.abs(node.transactionValue);
  // }
  // );

  return result;
}

/**
 * Detects cycles in Sankey data and logs them to the console.
 * Returns the same data structure with added cycle information.
 *
 * @param {Object} sankeyData - Object containing nodes and links arrays
 * @param {Array} sankeyData.nodes - Array of node objects
 * @param {Array} sankeyData.links - Array of link objects
 * @returns {Object} Sankey data with added cycles array
 */
function detectCycles(sankeyData) {
  const graph = new Map();
  const linkMap = new Map();

  // Build adjacency list
  sankeyData.links.forEach((link, idx) => {
    const source = typeof link.source === 'object' ? link.source.name : link.source;
    const target = typeof link.target === 'object' ? link.target.name : link.target;

    if (!graph.has(source)) graph.set(source, []);
    graph.get(source).push({ target, linkIndex: idx });
    linkMap.set(idx, link);
  });

  const cycles = [];
  const visiting = new Set();
  const visited = new Set();

  function dfs(node, path = [], pathLinks = []) {
    if (visiting.has(node)) {
      // Found a cycle
      const cycleStart = path.indexOf(node);
      const cyclePath = path.slice(cycleStart);
      const cycleLinks = pathLinks.slice(cycleStart);

      cycles.push({
        path: [...cyclePath, node],
        links: cycleLinks.map(idx => linkMap.get(idx))
      });
      return;
    }

    if (visited.has(node)) return;

    visiting.add(node);
    path.push(node);

    const neighbors = graph.get(node) || [];
    for (const { target, linkIndex } of neighbors) {
      pathLinks.push(linkIndex);
      dfs(target, path, pathLinks);
      pathLinks.pop();
    }

    path.pop();
    visiting.delete(node);
    visited.add(node);
  }

  // Start DFS from all nodes
  for (const node of graph.keys()) {
    if (!visited.has(node)) {
      dfs(node);
    }
  }

  return { ...sankeyData, cycles };
}

function fixCycles(sankeyData) {
  if (!sankeyData?.cycles?.length) return sankeyData;

  console.log(`Fixing ${sankeyData.cycles.length} cycle(s)...`);

  const flippedLinks = new Set();

  sankeyData.cycles.forEach((cycle, index) => {
    if (!cycle.links || cycle.links.length === 0) {
      console.warn(`Cycle ${index + 1}: No links found, skipping.`);
      return;
    }

    // Check if cycle still exists after previous flips
    const cycleStillExists = validateCycle(cycle.path, sankeyData.links);

    if (!cycleStillExists) {
      console.log(`  Cycle ${index + 1}: Already broken by previous fix, skipping.`);
      return;
    }

    // Find candidate links (those not already flipped)
    const candidateLinks = cycle.links.filter(link => !flippedLinks.has(link));

    if (candidateLinks.length === 0) {
      console.log(`  Cycle ${index + 1}: All links already flipped, skipping.`);
      return;
    }

    // Find minimum flow among candidates
    const minFlow = Math.min(...candidateLinks.map(link => Math.abs(link.value)));
    const minFlowLink = candidateLinks.find(link => Math.abs(link.value) === minFlow);

    // Mark as flipped
    flippedLinks.add(minFlowLink);

    // Swap source and target
    const temp = minFlowLink.source;
    minFlowLink.source = minFlowLink.target;
    minFlowLink.target = temp;
    minFlowLink.value = -minFlowLink.value;

    console.log(`  Cycle ${index + 1}: Reversed link (value: ${Math.abs(minFlowLink.value)})`);
  });

  delete sankeyData.cycles;
  return sankeyData;
}

// Helper function to validate if a cycle still exists
function validateCycle(path, links) {
  // Build current graph from links
  const graph = new Map();

  links.forEach(link => {
    const source = typeof link.source === 'object' ? link.source.name : link.source;
    const target = typeof link.target === 'object' ? link.target.name : link.target;

    if (!graph.has(source)) graph.set(source, []);
    graph.get(source).push(target);
  });

  // Check if we can traverse the cycle path
  for (let i = 0; i < path.length - 1; i++) {
    const from = path[i];
    const to = path[i + 1];

    const neighbors = graph.get(from);
    if (!neighbors || !neighbors.includes(to)) {
      // Link missing, cycle is broken
      return false;
    }
  }

  return true; // All links exist, cycle is still valid
}

export function transformToSankeyData(data = [], options = {}) {
  const aggregatedData = aggregateToSankeyData(data, options);
  console.log("Income - Credit Card Payment value RIGHT AFTER aggregation:",
  aggregatedData.nodes.find(n => n.id === "Income - Credit Card Payment")?.transactionValue);
  const rectifiedData = reverseNegativeLinks(aggregatedData);
  // const rectifiedData  = aggregatedData

  console.log("Income - Credit Card Payment value AFTER reversing negative links:",
  rectifiedData.nodes.find(n => n.id === "Income - Credit Card Payment")?.transactionValue);

  let currentData = rectifiedData;
  let iteration = 0;
  const maxIterations = 20;

  while (iteration < maxIterations) {
    const detected = detectCycles(currentData);

    if (!detected.cycles || detected.cycles.length === 0) {
      console.log(`✓ All cycles resolved after ${iteration} iteration(s)`);
      return detected;
    }

    console.log(`\nIteration ${iteration + 1}: Found ${detected.cycles.length} cycle(s)`);

    currentData = fixCycles(detected);
    console.log("Income - Credit Card Payment value AFTER fixing cycles:",
    currentData.nodes.find(n => n.id === "Income - Credit Card Payment")?.transactionValue);

    iteration++;
  }

  console.warn(`⚠ Max iterations (${maxIterations}) reached. ${currentData.cycles?.length || 0} cycle(s) remain.`);
  return currentData;
}

function assignNodesToColumns(nodes, links, columnCount, getAnchorLevel) {
      const { chain: longestPath, chainNodes } = findLongestPath(nodes, links, getAnchorLevel);

      const nodeToColumn = new Map();
      const columns = Array.from({ length: columnCount }, () => []);

      if (chainNodes.length === 0) {
        nodes.forEach((node, i) => {
          const col = Math.floor(i * columnCount / nodes.length);
          columns[col].push(node);
          nodeToColumn.set(node.id, col);
        });
        return { columns, nodeToColumn };
      }

      const nodeInDegree = new Map();
      const nodeOutDegree = new Map();
      const nodeTargets = new Map();
      const nodeSources = new Map();

      nodes.forEach(n => {
        nodeInDegree.set(n.id, 0);
        nodeOutDegree.set(n.id, 0);
        nodeTargets.set(n.id, new Set());
        nodeSources.set(n.id, new Set());
      });

      links.forEach(link => {
        const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
        const targetId = typeof link.target === 'object' ? link.target.id : link.target;

        nodeOutDegree.set(sourceId, nodeOutDegree.get(sourceId) + 1);
        nodeInDegree.set(targetId, nodeInDegree.get(targetId) + 1);
        nodeTargets.get(sourceId).add(targetId);
        nodeSources.get(targetId).add(sourceId);
      });

      const nodeById = new Map(nodes.map(n => [n.id, n]));

      const getAnchorColumn = (node) => {
        const level = getAnchorLevel(node);
        if (level === 0) return null;

        const sourceAnchorLevels = Array.from(nodeSources.get(node.id))
          .map(id => nodeById.get(id))
          .filter(n => n)
          .map(n => getAnchorLevel(n))
          .filter(lvl => lvl > 0);

        const targetAnchorLevels = Array.from(nodeTargets.get(node.id))
          .map(id => nodeById.get(id))
          .filter(n => n)
          .map(n => getAnchorLevel(n))
          .filter(lvl => lvl > 0);

        let isLeftSide = false;

        const hasHigherLevelSources = sourceAnchorLevels.some(lvl => lvl > level);
        const hasLowerLevelTargets = targetAnchorLevels.some(lvl => lvl < level);
        const hasLowerLevelSources = sourceAnchorLevels.some(lvl => lvl < level);
        const hasHigherLevelTargets = targetAnchorLevels.some(lvl => lvl > level);

        if (hasLowerLevelSources || hasHigherLevelTargets) {
          isLeftSide = false;
        } else if (hasHigherLevelSources || hasLowerLevelTargets) {
          isLeftSide = true;
        } else {
          const hasIncoming = nodeInDegree.get(node.id) > 0;
          const hasOutgoing = nodeOutDegree.get(node.id) > 0;

          if (hasOutgoing && !hasIncoming) {
            isLeftSide = true;
          } else if (hasIncoming && !hasOutgoing) {
            isLeftSide = false;
          } else if (hasIncoming && hasOutgoing) {
            const incomingSum = links
              .filter(l => (typeof l.target === 'object' ? l.target.id : l.target) === node.id)
              .reduce((sum, l) => sum + Math.abs(l.value), 0);

            const outgoingSum = links
              .filter(l => (typeof l.source === 'object' ? l.source.id : l.source) === node.id)
              .reduce((sum, l) => sum + Math.abs(l.value), 0);

            isLeftSide = outgoingSum > incomingSum;
          }
        }

        const maxLevel = Math.max(0, ...nodes.map(n => getAnchorLevel(n)));
        const offset = maxLevel - level;

        if (isLeftSide) {
          return offset;
        } else {
          return columnCount - 1 - offset;
        }
      };

      const canPlaceInColumn = (nodeId, col) => {
        const nodesInColumn = columns[col];
        for (const existingNode of nodesInColumn) {
          if (nodeTargets.get(nodeId).has(existingNode.id) ||
              nodeSources.get(nodeId).has(existingNode.id)) {
            return false;
          }
        }
        return true;
      };

      const findValidColumn = (nodeId, preferredCol, minCol = 0, maxCol = columnCount - 1) => {
        if (preferredCol >= minCol && preferredCol <= maxCol && canPlaceInColumn(nodeId, preferredCol)) {
          return preferredCol;
        }

        for (let offset = 1; offset < columnCount; offset++) {
          const higherCol = preferredCol + offset;
          if (higherCol <= maxCol && canPlaceInColumn(nodeId, higherCol)) {
            return higherCol;
          }

          const lowerCol = preferredCol - offset;
          if (lowerCol >= minCol && canPlaceInColumn(nodeId, lowerCol)) {
            return lowerCol;
          }
        }

        return preferredCol;
      };

      const maxAnchorLevel = Math.max(0, ...nodes.map(n => getAnchorLevel(n)));
      const chainNodeIds = new Set(longestPath);
      const anchorNodesByLevel = new Map();
      const nonAnchorChain = [];

      for (let i = 1; i <= maxAnchorLevel; i++) {
        anchorNodesByLevel.set(i, { left: [], right: [] });
      }

      chainNodes.forEach(node => {
        const level = getAnchorLevel(node);

        if (level > 0) {
          const anchorCol = getAnchorColumn(node);
          const isLeftSide = anchorCol < columnCount / 2;

          if (isLeftSide) {
            anchorNodesByLevel.get(level).left.push(node);
          } else {
            anchorNodesByLevel.get(level).right.push(node);
          }
        } else {
          nonAnchorChain.push(node);
        }
      });

      for (let level = maxAnchorLevel; level >= 1; level--) {
        const { left, right } = anchorNodesByLevel.get(level);

        left.forEach(node => {
          const col = maxAnchorLevel - level;
          const validCol = findValidColumn(node.id, col, col, col);
          columns[validCol].push(node);
          nodeToColumn.set(node.id, validCol);
        });

        right.forEach(node => {
          const col = columnCount - 1 - (maxAnchorLevel - level);
          const validCol = findValidColumn(node.id, col, col, col);
          columns[validCol].push(node);
          nodeToColumn.set(node.id, validCol);
        });
      }

      const leftReserved = maxAnchorLevel;
      const rightReserved = maxAnchorLevel;
      const middleStart = leftReserved;
      const middleEnd = columnCount - rightReserved - 1;
      const middleColumns = middleEnd - middleStart;

      if (nonAnchorChain.length > 0) {
        nonAnchorChain.forEach((node, idx) => {
          let preferredCol;

          if (middleColumns <= 0) {
            preferredCol = Math.floor(columnCount / 2);
          } else if (nonAnchorChain.length === 1) {
            preferredCol = Math.floor((middleStart + middleEnd) / 2);
          } else {
            const ratio = idx / (nonAnchorChain.length - 1);
            preferredCol = middleStart + Math.round(ratio * middleColumns);
          }

          const col = findValidColumn(node.id, preferredCol, middleStart, middleEnd);
          columns[col].push(node);
          nodeToColumn.set(node.id, col);
        });
      }

      const remainingNodes = nodes.filter(node => !chainNodeIds.has(node.id));

      remainingNodes.forEach(node => {
        const level = getAnchorLevel(node);

        if (level > 0) {
          const anchorCol = getAnchorColumn(node);
          const col = findValidColumn(node.id, anchorCol, anchorCol, anchorCol);
          columns[col].push(node);
          nodeToColumn.set(node.id, col);
        } else {
          const connectedCols = [];

          links.forEach(link => {
            const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
            const targetId = typeof link.target === 'object' ? link.target.id : link.target;

            if (sourceId === node.id && nodeToColumn.has(targetId)) {
              connectedCols.push(nodeToColumn.get(targetId));
            }
            if (targetId === node.id && nodeToColumn.has(sourceId)) {
              connectedCols.push(nodeToColumn.get(sourceId));
            }
          });

          let preferredCol;
          if (connectedCols.length > 0) {
            preferredCol = Math.round(connectedCols.reduce((a, b) => a + b, 0) / connectedCols.length);
          } else {
            preferredCol = Math.floor(columnCount / 2);
          }

          const col = findValidColumn(node.id, preferredCol, middleStart, middleEnd);
          columns[col].push(node);
          nodeToColumn.set(node.id, col);
        }
      });

      return { columns, nodeToColumn };
    }

function findTopAnchorNodesByColumn(nodes, nodeToColumn, getAnchorLevel, topN = 10) {
  // for each node with anchorLevel > 0, sort into columns based on nodeToColumn mapping
  // then retain the top n nodes by transactionValue in each column
      const colNodes = new Map();

      // first, get all nodes with anchorLevel > 0 into their columns
      nodes.forEach(node => {
        if (getAnchorLevel(node) > 0) {
          const col = nodeToColumn.get(node.id);
          if (col === undefined) return;

          if (!colNodes.has(col)) {
            colNodes.set(col, []);
          }

          colNodes.get(col).push(node);
        }
      });

      // now, for each column, sort nodes by transactionValue and retain top N
      const topNodes = new Map();

      colNodes.forEach((nodesInCol, col) => {
        nodesInCol.sort((a, b) => b.transactionValue - a.transactionValue);
        topNodes.set(col, nodesInCol.slice(0, topN));
      });

      return topNodes;
}

function aggregateTopAnchorNodes(nodes, links, nodeToColumn, getAnchorLevel, anchorLevelNames, topN, colorPalette) {

  if (topN <= 0) return { nodes, links, nodeToColumn };
  // Group nodes by column and anchor level
  const nodesByColumnAndLevel = new Map();

  nodes.forEach(node => {
    const column = nodeToColumn.get(node.id);
    const anchorLevel = getAnchorLevel(node);

    if (column === undefined || anchorLevel === undefined) return;

    const key = `${column}-${anchorLevel}`;
    if (!nodesByColumnAndLevel.has(key)) {
      nodesByColumnAndLevel.set(key, []);
    }
    nodesByColumnAndLevel.get(key).push(node);
  });

  // Determine which nodes to keep and which to aggregate
  const nodesToKeep = new Set();
  const nodesToAggregate = new Map(); // key -> array of nodes to aggregate

  nodesByColumnAndLevel.forEach((nodesInGroup, key) => {
    // Sort by absolute transaction value (descending)
    const sorted = [...nodesInGroup].sort((a, b) => {
      return Math.abs(b.transactionValue || 0) - Math.abs(a.transactionValue || 0);
    });

    // Keep top N, aggregate the rest
    const toKeep = sorted.slice(0, topN);
    const toAggregate = sorted.slice(topN);

    toKeep.forEach(node => nodesToKeep.add(node.id));

    if (toAggregate.length > 0) {
      nodesToAggregate.set(key, toAggregate);
    }
  });

  // Build new nodes array
  const newNodes = [];
  const aggregatedNodeMap = new Map(); // key -> aggregated node

  // Add nodes that are kept
  nodes.forEach(node => {
    if (nodesToKeep.has(node.id)) {
      newNodes.push(node);
    }
  });

  // Create aggregated nodes
  nodesToAggregate.forEach((nodesToAgg, key) => {
    const [columnStr, anchorLevelStr] = key.split('-');
    const column = parseInt(columnStr);
    const anchorLevel = parseInt(anchorLevelStr);

    // Sum up transaction values
    const aggregatedValue = nodesToAgg.reduce((sum, node) => {
      return sum + (node.transactionValue || 0);
    }, 0);

    const aggregatedTransactionIDs = new Set();
    // add all transactionIDs from aggregated nodes
    nodesToAgg.forEach(node => {
      if (node.transactionIDs && node.transactionIDs instanceof Set) {
        node.transactionIDs.forEach(id => aggregatedTransactionIDs.add(id));
      }
    });
    const aggregatedNode = {
      id: `aggregated-col${column}-level${anchorLevel}`,
      transactionValue: aggregatedValue,
      transactionIDs: aggregatedTransactionIDs,
      name: `Other ${anchorLevelNames[anchorLevel-1] || `Level ${anchorLevel}`}`,
      anchorLevel: anchorLevel,
      isAggregated: true,
      color: colorPalette[(column + anchorLevel) % colorPalette.length] // Assign color based on column and level
    };

    newNodes.push(aggregatedNode);
    aggregatedNodeMap.set(key, aggregatedNode);
  });

  // Build new links array
  const newLinks = [];
  const aggregatedNodeIds = new Set(
    Array.from(nodesToAggregate.values()).flat().map(n => n.id)
  );

  links.forEach(link => {
    const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
    const targetId = typeof link.target === 'object' ? link.target.id : link.target;

    const sourceAggregated = aggregatedNodeIds.has(sourceId);
    const targetAggregated = aggregatedNodeIds.has(targetId);

    // Determine new source
    let newSource = link.source;
    if (sourceAggregated) {
      const sourceNode = nodes.find(n => n.id === sourceId);
      const sourceColumn = nodeToColumn.get(sourceId);
      const sourceLevel = getAnchorLevel(sourceNode);
      const key = `${sourceColumn}-${sourceLevel}`;
      newSource = aggregatedNodeMap.get(key);
    }

    // Determine new target
    let newTarget = link.target;
    if (targetAggregated) {
      const targetNode = nodes.find(n => n.id === targetId);
      const targetColumn = nodeToColumn.get(targetId);
      const targetLevel = getAnchorLevel(targetNode);
      const key = `${targetColumn}-${targetLevel}`;
      newTarget = aggregatedNodeMap.get(key);
    }

    // Skip self-loops
    const newSourceId = typeof newSource === 'object' ? newSource.id : newSource;
    const newTargetId = typeof newTarget === 'object' ? newTarget.id : newTarget;
    if (newSourceId === newTargetId) {
      return;
    }

    // Check if we already have a link between these nodes
    const existingLink = newLinks.find(l => {
      const lSourceId = typeof l.source === 'object' ? l.source.id : l.source;
      const lTargetId = typeof l.target === 'object' ? l.target.id : l.target;
      return lSourceId === newSourceId && lTargetId === newTargetId;
    });

    if (existingLink) {
      // Merge values
      existingLink.value += link.value;
      existingLink.transactionIDs = new Set([
        ...existingLink.transactionIDs,
        ...(link.transactionIDs || [])
      ]);
    } else {
      // Create new link
      newLinks.push({
        source: newSource,
        target: newTarget,
        value: link.value,
        transactionIDs: link.transactionIDs || new Set()
      });
    }
  });

  // Rebuild nodeToColumn map
  const newNodeToColumn = new Map();

  // Add kept nodes
  nodes.forEach(node => {
    if (nodesToKeep.has(node.id)) {
      const column = nodeToColumn.get(node.id);
      if (column !== undefined) {
        newNodeToColumn.set(node.id, column);
      }
    }
  });

  // Add aggregated nodes
  aggregatedNodeMap.forEach((aggNode, key) => {
    const [columnStr] = key.split('-');
    const column = parseInt(columnStr);
    newNodeToColumn.set(aggNode.id, column);
  });

  return {
    nodes: newNodes,
    links: newLinks,
    nodeToColumn: newNodeToColumn
  };
}

function findLongestPath(nodes, links, getAnchorLevel) {
      const graph = new Map();
      const inDegree = new Map();

      nodes.forEach(node => {
        graph.set(node.id, []);
        inDegree.set(node.id, 0);
      });

      links.forEach(link => {
        const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
        const targetId = typeof link.target === 'object' ? link.target.id : link.target;

        if (graph.has(sourceId) && graph.has(targetId)) {
          graph.get(sourceId).push(targetId);
          inDegree.set(targetId, inDegree.get(targetId) + 1);
        }
      });

      const nodeMap = new Map(nodes.map(n => [n.id, n]));
      let globalLongestPath = [];

      const startNodes = nodes.filter(node => {
        return inDegree.get(node.id) === 0 || getAnchorLevel(node) > 0;
      });

      startNodes.forEach(startNode => {
        function findPathsFrom(nodeId, visited) {
          const neighbors = graph.get(nodeId) || [];
          const validNeighbors = neighbors.filter(nId => {
            if (visited.has(nId)) return false;
            return getAnchorLevel(nodeMap.get(nId)) === 0;
          });

          if (validNeighbors.length === 0) {
            return [[nodeId]];
          }

          const allPaths = [];
          validNeighbors.forEach(neighborId => {
            const newVisited = new Set(visited);
            newVisited.add(nodeId);
            const subPaths = findPathsFrom(neighborId, newVisited);
            subPaths.forEach(subPath => {
              allPaths.push([nodeId, ...subPath]);
            });
          });

          return allPaths;
        }

        const paths = findPathsFrom(startNode.id, new Set());
        paths.forEach(path => {
          if (path.length > globalLongestPath.length) {
            globalLongestPath = path;
          }
        });
      });

      const pathNodes = globalLongestPath.map(id => nodeMap.get(id));
      const maxAnchorLevel = Math.max(0, ...nodes.map(n => getAnchorLevel(n)));
      const anchorColumnsNeeded = maxAnchorLevel * 2;

      let columnCount = globalLongestPath.length + anchorColumnsNeeded;

      if (pathNodes.length > 0) {
        const firstNode = pathNodes[0];
        const lastNode = pathNodes[pathNodes.length - 1];

        if (getAnchorLevel(firstNode) === 0) columnCount++;
        if (getAnchorLevel(lastNode) === 0) columnCount++;
      }

      return { chain: globalLongestPath, columnCount, chainNodes: pathNodes };
    }

export function customSankeyLayout(nodes, links, width, height, margin, nodeWidth, nodePadding, getAnchorLevel, anchorLevelNames, topN, colorPalette) {
  // resolveOverlaps - shifts nodes to prevent overlap, maintains target heights strictly
  function resolveOverlaps(column, minY, maxY, padding) {
    column.sort((a, b) => a.y0 - b.y0);

    // First pass: ensure no node starts above minY
    column.forEach(node => {
      if (node.y0 < minY) {
        const nodeHeight = node.y1 - node.y0;
        node.y0 = minY;
        node.y1 = node.y0 + nodeHeight;
      }
    });

    // Second pass: shift overlapping nodes down
    for (let i = 1; i < column.length; i++) {
      const prev = column[i - 1];
      const curr = column[i];
      const minCurrentY = prev.y1 + padding;

      if (curr.y0 < minCurrentY) {
        const shift = minCurrentY - curr.y0;
        const nodeHeight = curr.targetHeight || (curr.y1 - curr.y0);
        curr.y0 += shift;
        curr.y1 = curr.y0 + nodeHeight;
      }
    }

    // Third pass: if the last node extends beyond maxY, compress the entire column
    if (column.length > 0 && column[column.length - 1].y1 > maxY) {
      const totalNodeHeight = column.reduce((sum, node) => {
        return sum + (node.targetHeight || (node.y1 - node.y0));
      }, 0);
      const availableSpaceForGaps = (maxY - minY) - totalNodeHeight;
      const minGap = Math.max(2, padding * 0.2);

      if (column.length > 1) {
        const requiredMinGaps = (column.length - 1) * minGap;

        if (availableSpaceForGaps >= requiredMinGaps) {
          const newGap = availableSpaceForGaps / (column.length - 1);
          let currentY = minY;
          column.forEach((node, i) => {
            const nodeHeight = node.targetHeight || (node.y1 - node.y0);
            node.y0 = currentY;
            node.y1 = currentY + nodeHeight;
            currentY = node.y1 + (i < column.length - 1 ? newGap : 0);
          });
        } else {
          let currentY = minY;
          column.forEach((node, i) => {
            const nodeHeight = node.targetHeight || (node.y1 - node.y0);
            node.y0 = currentY;
            node.y1 = currentY + nodeHeight;
            currentY = node.y1 + (i < column.length - 1 ? minGap : 0);
          });
        }
      } else {
        // Single node that's too tall - clamp it to bounds
        const nodeHeight = column[0].y1 - column[0].y0;
        column[0].y0 = minY;
        column[0].y1 = Math.min(minY + nodeHeight, maxY);
      }
    }
  }

  // Convert link indices to node references
  links.forEach(link => {
    if (typeof link.source === 'number') {
      link.source = nodes[link.source];
    }
    if (typeof link.target === 'number') {
      link.target = nodes[link.target];
    }
  });

  // Determine column layout
  const { columnCount } = findLongestPath(nodes, links, getAnchorLevel);
  let { columns, nodeToColumn } = assignNodesToColumns(nodes, links, columnCount, getAnchorLevel);

  // console.log(`Assigned ${nodes.length} nodes to ${columnCount} columns`);
  // console.log(`Before aggregation: ${nodes.length} nodes and ${links.length} links`);
  // console.log(structuredClone({"nodes_before": nodes,
  //         "nodeToColumn_before": nodeToColumn,
  //         "links_before": links}));

  // nodes.forEach(node => {
  //   console.log("Node before aggregation:", JSON.stringify(node));
  // });
  // links.forEach(link => {
  //   console.log("Link before aggregation:", JSON.stringify(link));
  // });
  const aggregationResult = aggregateTopAnchorNodes(nodes, links, nodeToColumn, getAnchorLevel, anchorLevelNames, topN, colorPalette);

  const nodes2 = aggregationResult.nodes;
  const links2 = aggregationResult.links;
  const nodeToColumn2 = aggregationResult.nodeToColumn;

  // console.log(`After aggregation: ${nodes2.length} nodes and ${links2.length} links`);
  // console.log(structuredClone({"nodes_after": nodes2,
  //         "nodeToColumn_after": nodeToColumn2,
  //         "links_after": links2
  // }));
  // nodes2.forEach(node => {
  //   console.log("Node after aggregation:", JSON.stringify(node));
  // });
  // links2.forEach(link => {
  //   console.log("Link after aggregation:", JSON.stringify(link));
  // });

  nodes = nodes2;
  links = links2;
  nodeToColumn = nodeToColumn2;

  columns = Array.from({ length: columnCount }, () => []);
nodes.forEach(node => {
  const col = nodeToColumn.get(node.id);
  if (col !== undefined) {
    columns[col].push(node);
  }
})


  // Compact-column remap. `columnCount` was sized for the worst-case longest
  // path through the unfiltered graph; once the user filters (e.g. to a
  // single category), many columns are empty but still consume horizontal
  // space, jamming the rendered content to one edge. Build a mapping from
  // each original column index to a compacted index spanning only the
  // non-empty columns, preserve relative left-to-right order, then space
  // the compacted columns evenly across the available width so the chart
  // fills (and is centered within) the canvas regardless of filter state.
  const usedColumnIndices = [];
  for (let i = 0; i < columns.length; i++) {
    if (columns[i].length > 0) usedColumnIndices.push(i);
  }
  const compactColumnIndex = new Map();
  usedColumnIndices.forEach((origCol, newCol) => {
    compactColumnIndex.set(origCol, newCol);
  });
  const usedColumnCount = Math.max(1, usedColumnIndices.length);

  // Calculate horizontal positions with proportional scaling to reduce bezier curve steepness
  // Scale down both dimensions by same factor to maintain gentle curves while fitting in canvas
  const scaleFactor = 1.5;
  const scaledNodeWidth = nodeWidth / scaleFactor;
  const availableWidth = (width - margin.left - margin.right - scaledNodeWidth) / scaleFactor;
  // Increase column spacing by additional factor to make curves even gentler
  const baseColumnSpacing = usedColumnCount > 1 ? availableWidth / (usedColumnCount - 1) : 0;
  const columnSpacing = baseColumnSpacing * 1.5; // 1.5x more spacing between columns

  // Calculate horizontal centering offset based on the COMPACTED layout. With
  // 1 column the entire chart is one node-width wide and gets centered;
  // with N>1 columns the layout spans full available width (offset ≈ 0).
  const totalChartWidth = (usedColumnCount - 1) * columnSpacing + scaledNodeWidth;
  const horizontalOffset = (width - margin.left - margin.right - totalChartWidth) / 2;

  nodes.forEach(node => {
    const origCol = nodeToColumn.get(node.id);
    const col = compactColumnIndex.has(origCol) ? compactColumnIndex.get(origCol) : origCol;
    node.x0 = margin.left + horizontalOffset + col * columnSpacing;
    node.x1 = node.x0 + scaledNodeWidth;

    if (node.value === undefined) {
      node.value = Math.abs(node.transactionValue || 0);
    }
  });

  // Calculate flow values for each node
  // The flow value determines the node height and must accommodate all connected links
  nodes.forEach(node => {
    let incomingFlow = 0;
    let outgoingFlow = 0;

    links.forEach(link => {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;

      if (sourceId === node.id) {
        outgoingFlow += Math.abs(link.value);
      }
      if (targetId === node.id) {
        incomingFlow += Math.abs(link.value);
      }
    });

    // Node must be tall enough to accommodate the larger of incoming or outgoing flows
    node.flowValue = Math.max(incomingFlow, outgoingFlow);

    if (node.flowValue === 0) {
      node.flowValue = Math.abs(node.transactionValue || 0);
    }
  });

  // Calculate vertical scale
  // Scale down available height proportionally to maintain gentle curve geometry
  const availableHeight = (height - margin.top - margin.bottom) / scaleFactor;

  const columnPaddings = columns.map(column =>
    Math.max(0, (column.length - 1) * nodePadding / scaleFactor)
  );

  const columnRequirements = columns.map((column, colIdx) => {
    const totalNodeHeight = column.reduce((sum, node) => sum + node.flowValue, 0);
    return totalNodeHeight + columnPaddings[colIdx];
  });

  const maxRequirement = Math.max(...columnRequirements);

  // Calculate pixels per unit so that the busiest column exactly fits
  // Add a safety factor to ensure we don't exceed available height
  const pixelsPerUnit = maxRequirement > 0 ? (availableHeight * 0.95) / maxRequirement : 1;

  // Calculate path lengths for sorting
  const nodePathLength = new Map();

  function calculatePathLength(nodeId, visited = new Set()) {
    if (nodePathLength.has(nodeId)) {
      return nodePathLength.get(nodeId);
    }

    if (visited.has(nodeId)) {
      return 0;
    }

    visited.add(nodeId);

    const incomingLinks = links.filter(link => link.target.id === nodeId);

    if (incomingLinks.length === 0) {
      nodePathLength.set(nodeId, 0);
      return 0;
    }

    const maxParentLength = Math.max(
      ...incomingLinks.map(link => calculatePathLength(link.source.id, new Set(visited)))
    );

    const pathLength = maxParentLength + 1;
    nodePathLength.set(nodeId, pathLength);
    return pathLength;
  }

  nodes.forEach(node => calculatePathLength(node.id));

  // Initial vertical positioning
  columns.forEach(column => {
    column.sort((a, b) => {
      const pathDiff = (nodePathLength.get(a.id) || 0) - (nodePathLength.get(b.id) || 0);
      if (pathDiff !== 0) return pathDiff;
      return Math.abs(b.transactionValue || b.value || 0) -
             Math.abs(a.transactionValue || a.value || 0);
    });

    let currentY = margin.top;
    column.forEach(node => {
      const nodeHeight = node.flowValue * pixelsPerUnit;
      node.y0 = currentY;
      node.y1 = currentY + nodeHeight;
      currentY = node.y1 + nodePadding / scaleFactor;
    });
  });

  // Relaxation iterations to align nodes with their connections
  const relaxIterations = 6;
  const alpha = 0.3;

  for (let iter = 0; iter < relaxIterations; iter++) {
    // Forward pass: align with targets
    columns.forEach((column, colIndex) => {
      column.forEach(node => {
        const outgoingLinks = links.filter(link => link.source.id === node.id);
        if (outgoingLinks.length > 0) {
          const totalValue = outgoingLinks.reduce((sum, link) => sum + Math.abs(link.value), 0);
          const weightedY = outgoingLinks.reduce((sum, link) => {
            const targetCenter = (link.target.y0 + link.target.y1) / 2;
            return sum + targetCenter * Math.abs(link.value);
          }, 0) / totalValue;

          const nodeHeight = node.y1 - node.y0;
          const nodeCenter = (node.y0 + node.y1) / 2;
          const newCenter = nodeCenter + alpha * (weightedY - nodeCenter);

          node.y0 = newCenter - nodeHeight / 2;
          node.y1 = newCenter + nodeHeight / 2;
        }
      });

      resolveOverlaps(column, margin.top, margin.top + availableHeight, nodePadding / scaleFactor);
    });

    // Backward pass: align with sources
    for (let colIndex = columns.length - 1; colIndex >= 0; colIndex--) {
      const column = columns[colIndex];
      column.forEach(node => {
        const incomingLinks = links.filter(link => link.target.id === node.id);
        if (incomingLinks.length > 0) {
          const totalValue = incomingLinks.reduce((sum, link) => sum + Math.abs(link.value), 0);
          const weightedY = incomingLinks.reduce((sum, link) => {
            const sourceCenter = (link.source.y0 + link.source.y1) / 2;
            return sum + sourceCenter * Math.abs(link.value);
          }, 0) / totalValue;

          const nodeHeight = node.y1 - node.y0;
          const nodeCenter = (node.y0 + node.y1) / 2;
          const newCenter = nodeCenter + alpha * (weightedY - nodeCenter);

          node.y0 = newCenter - nodeHeight / 2;
          node.y1 = newCenter + nodeHeight / 2;
        }
      });

      resolveOverlaps(column, margin.top, margin.top + availableHeight, nodePadding);
    }
  }

  // Calculate vertical centering offset after relaxation
  const maxY = Math.max(...nodes.map(n => n.y1));
  const minY = Math.min(...nodes.map(n => n.y0));
  const totalChartHeight = maxY - minY;
  const fullHeight = height - margin.top - margin.bottom;
  const verticalOffset = (fullHeight - totalChartHeight) / 2;

  // Apply vertical centering to all nodes
  nodes.forEach(node => {
    node.y0 += verticalOffset;
    node.y1 += verticalOffset;
  });

  // Calculate link widths (scaled down)
  links.forEach(link => {
    link.width = Math.abs(link.value) * pixelsPerUnit;
  });

  // Position link endpoints
  const linksBySource = new Map();
  const linksByTarget = new Map();

  links.forEach(link => {
    if (!linksBySource.has(link.source.id)) linksBySource.set(link.source.id, []);
    if (!linksByTarget.has(link.target.id)) linksByTarget.set(link.target.id, []);

    linksBySource.get(link.source.id).push(link);
    linksByTarget.get(link.target.id).push(link);
  });

  // Position source link endpoints
  nodes.forEach(node => {
    const sourceLinks = (linksBySource.get(node.id) || []).sort((a, b) => {
      const aTargetY = (a.target.y0 + a.target.y1) / 2;
      const bTargetY = (b.target.y0 + b.target.y1) / 2;
      return aTargetY - bTargetY;
    });

    if (sourceLinks.length > 0) {
      const nodeHeight = node.y1 - node.y0;
      const totalLinkWidth = sourceLinks.reduce((sum, link) => sum + link.width, 0);

      // Calculate scale factor if links need to be compressed
      const scale = totalLinkWidth > nodeHeight ? nodeHeight / totalLinkWidth : 1;

      let currentY = node.y0;

      sourceLinks.forEach(link => {
        const scaledWidth = link.width * scale;
        link.y0 = currentY + scaledWidth / 2;
        currentY += scaledWidth;
      });
    }
  });

  // Position target link endpoints
  nodes.forEach(node => {
    const targetLinks = (linksByTarget.get(node.id) || []).sort((a, b) => {
      const aSourceY = (a.source.y0 + a.source.y1) / 2;
      const bSourceY = (b.source.y0 + b.source.y1) / 2;
      return aSourceY - bSourceY;
    });

    if (targetLinks.length > 0) {
      const nodeHeight = node.y1 - node.y0;
      const totalLinkWidth = targetLinks.reduce((sum, link) => sum + link.width, 0);

      // Calculate scale factor if links need to be compressed
      const scale = totalLinkWidth > nodeHeight ? nodeHeight / totalLinkWidth : 1;

      let currentY = node.y0;

      targetLinks.forEach(link => {
        const scaledWidth = link.width * scale;
        link.y1 = currentY + scaledWidth / 2;
        currentY += scaledWidth;
      });
    }
  });

  return { nodes, links };
}

// ============================================================================
// Reconfigurable tiered flow-of-funds (Sankey Refresh — Trello FkCeGYdu).
//
// Each granularity gets its own lateral column. The Account is the fixed
// center pivot; inflow tiers fan in from the left, outflow tiers fan out to
// the right. Internal transfers are ALWAYS excluded here (they get a separate
// Chord view later). This is deliberately independent of the legacy
// `transformToSankeyData`/`customSankeyLayout` pair (still used by
// report-transfer-analysis.js) so those keep their current behavior.
// ============================================================================

// Lateral tier order. `AC` (Account) is always present and never removable —
// it is the pivot every inflow ends at and every outflow starts from.
export const TIER_DEFS = [
  { key: 'IM', side: 'in',   name: 'Merchant',    field: 'merchantName',    fallback: 'Unknown Merchant' },
  { key: 'IS', side: 'in',   name: 'Subcategory', field: 'subcategoryName', fallback: 'Uncategorized' },
  { key: 'IC', side: 'in',   name: 'Category',    field: 'categoryName',    fallback: 'Uncategorized' },
  { key: 'AC', side: 'acct', name: 'Account',     field: 'bankAccountName', fallback: 'Unknown Account' },
  { key: 'OC', side: 'out',  name: 'Category',    field: 'categoryName',    fallback: 'Uncategorized' },
  { key: 'OS', side: 'out',  name: 'Subcategory', field: 'subcategoryName', fallback: 'Uncategorized' },
  { key: 'OM', side: 'out',  name: 'Merchant',    field: 'merchantName',    fallback: 'Unknown Merchant' },
];

// d3 mutates node.color (d3.color(...).darker()), so these must be concrete
// hex, not CSS var() tokens. Values mirror the design-system palette
// (navy-900, teal/green family for inflow, warm family for outflow).
const ACCT_COLOR = '#1B2F5E';
const OTHER_COLOR = '#94a3b8';
// Inflow = teal/green family, Outflow = warm family (orange/amber/red/brown).
// Keeping each side to one hue family makes the Inflows/Accounts/Outflows
// legend swatches representative of the ribbons and the flow direction read
// at a glance.
// The legend swatches (report-sankey-analysis.module.css) use design tokens
// that mirror these: accent-500 ≈ inflow teal, navy-900 = account, warning-600
// ≈ outflow orange. Keep them in sync if these palettes change.
const INFLOW_PALETTE = ['#0e7490', '#14b8a6', '#2dd4bf', '#0891b2', '#16794a', '#15803d'];
const OUTFLOW_PALETTE = ['#b45309', '#c2410c', '#be123c', '#d97706', '#9a3412', '#a16207', '#b91c1c', '#92400e'];

const hashIndex = (str, mod) => {
  let h = 0;
  for (let i = 0; i < String(str).length; i++) h = (h * 31 + str.charCodeAt(i)) & 0x7fffffff;
  return mod > 0 ? h % mod : 0;
};

const tierColor = (side, label) => {
  if (side === 'acct') return ACCT_COLOR;
  const palette = side === 'in' ? INFLOW_PALETTE : OUTFLOW_PALETTE;
  return palette[hashIndex(label, palette.length)];
};

const tierDisplayName = (t) =>
  t.side === 'acct' ? 'Account' : `${t.side === 'in' ? 'Inflow' : 'Outflow'} ${t.name}`;

/**
 * Build Sankey {nodes, links} from enriched transactions for the tiered model.
 *
 * @param {Array}  transactions  enriched (filterable) transactions — each with
 *                 merchantName / subcategoryName / categoryName / bankAccountName /
 *                 amount (sign = inflow>0 / outflow<0) / hasTransferPartner /
 *                 transaction.id.
 * @param {Object} opts
 * @param {Set|Array} opts.enabledTiers  tier keys currently shown (AC implicit).
 * @param {boolean}   opts.separateAccounts  false collapses accounts to one node.
 * @returns {{nodes, links, anchorLevelNames, colCount}}
 */
export function buildTieredSankeyData(transactions = [], { enabledTiers = [], separateAccounts = true } = {}) {
  const enabled = enabledTiers instanceof Set ? enabledTiers : new Set(enabledTiers);
  const activeTiers = TIER_DEFS.filter(t => t.key === 'AC' || enabled.has(t.key));
  const colOf = new Map(activeTiers.map((t, i) => [t.key, i]));
  const leftTiers = activeTiers.filter(t => t.side === 'in');
  const rightTiers = activeTiers.filter(t => t.side === 'out');
  const acctDef = TIER_DEFS.find(t => t.key === 'AC');

  const nodes = new Map();
  const links = new Map();

  const nodeId = (key, label) => `${key}::${label}`;
  const ensureNode = (def, label) => {
    const id = nodeId(def.key, label);
    let n = nodes.get(id);
    if (!n) {
      n = {
        id,
        name: label,
        tierKey: def.key,
        col: colOf.get(def.key),
        anchorLevel: colOf.get(def.key) + 1,
        color: tierColor(def.side, label),
        valueIn: 0,
        valueOut: 0,
        transactionIDs: new Set(),
      };
      nodes.set(id, n);
    }
    return n;
  };
  const addLink = (s, t, value, txnId) => {
    const key = `${s.id}|${t.id}`;
    let l = links.get(key);
    if (!l) { l = { source: s.id, target: t.id, value: 0, transactionIDs: new Set() }; links.set(key, l); }
    l.value += value;
    s.valueOut += value;
    t.valueIn += value;
    if (txnId != null) { l.transactionIDs.add(txnId); s.transactionIDs.add(txnId); t.transactionIDs.add(txnId); }
  };

  const labelFor = (def, txn) => (def.key === 'AC'
    ? (separateAccounts ? (txn[def.field] || def.fallback) : 'All Accounts')
    : (txn[def.field] || def.fallback));

  (transactions || []).forEach(txn => {
    if (!txn || txn.hasTransferPartner) return; // transfers excluded — always
    // Non-cash rows (investment buys/sells/sweeps, valuations) are not money in
    // or out — excluded like transfers (categorization-segments ADR-001).
    if (isNonCashTransaction(txn)) return;
    // Coerce to a finite number. A truthy-but-non-numeric amount (e.g. a
    // currency-formatted string like "1,234.56") slips through `txn.amount || 0`
    // unchanged, then Math.abs()es to NaN. A single NaN link value propagates
    // through the layout's relaxation pass and blanks the ENTIRE money-flow
    // chart (every node's y goes NaN → all labels stack at the top, no ribbons).
    // Such a row has no placeable magnitude, so drop it rather than let one bad
    // datum take down the whole view.
    const amount = Number(txn.amount);
    if (!Number.isFinite(amount) || amount === 0) return;
    const txnId = txn.transaction?.id;
    const value = Math.abs(amount);

    // inflow (amount>0): merchant→subcat→category→ACCOUNT
    // outflow (amount<0): ACCOUNT→category→subcat→merchant
    const chainTiers = amount > 0
      ? [...leftTiers, acctDef]
      : [acctDef, ...rightTiers];
    const chain = chainTiers.map(def => ensureNode(def, labelFor(def, txn)));
    for (let i = 0; i < chain.length - 1; i++) addLink(chain[i], chain[i + 1], value, txnId);
  });

  const nodeList = [...nodes.values()].map(n => {
    n.value = Math.max(n.valueIn, n.valueOut);
    n.transactionValue = n.value;
    return n;
  });
  const linkList = [...links.values()];
  const anchorLevelNames = activeTiers.map(tierDisplayName);
  return { nodes: nodeList, links: linkList, anchorLevelNames, colCount: activeTiers.length };
}

/**
 * Deterministic fixed-tier-column layout. Each node's column = its tier's
 * lateral index (Account centered). Produces d3-sankey-compatible geometry
 * (node x0/x1/y0/y1 + link y0/y1/width) that SankeyChart renders directly.
 * Signature matches the `customSankeyLayout` prop the renderer calls.
 */
export function tieredSankeyLayout(
  nodesIn, linksIn, width, height, margin, nodeWidth, nodePadding,
  getAnchorLevel, anchorLevelNames, topN /* , colorPalette */
) {
  const NODE_W = nodeWidth || 18;
  const PAD_REQUESTED = nodePadding || 16;

  let nodes = nodesIn.slice();
  const byId = new Map(nodes.map(n => [n.id, n]));
  const resolve = (ref) => (typeof ref === 'number' ? nodesIn[ref]
    : (ref && typeof ref === 'object') ? ref : byId.get(ref));
  let links = linksIn
    .map(l => ({ ...l, source: resolve(l.source), target: resolve(l.target) }))
    .filter(l => l.source && l.target && l.source !== l.target);

  // ---- per-column top-N: merge the long tail into an "Other <tier>" node ----
  if (topN && topN > 0) {
    const byCol = new Map();
    nodes.forEach(n => { (byCol.get(n.col) || byCol.set(n.col, []).get(n.col)).push(n); });
    const mergeMap = new Map();
    const aggregated = [];
    byCol.forEach((colNodes, col) => {
      if (colNodes.length <= topN) return;
      const tail = colNodes.slice().sort((a, b) => (b.value || 0) - (a.value || 0)).slice(topN);
      if (!tail.length) return;
      const tierName = (anchorLevelNames && anchorLevelNames[col]) || `Level ${col + 1}`;
      const agg = {
        id: `other::col${col}`, name: `Other ${tierName}`, col, anchorLevel: col + 1,
        color: OTHER_COLOR, value: 0, transactionValue: 0, isAggregated: true,
        transactionIDs: new Set(),
      };
      tail.forEach(n => {
        agg.value += n.value || 0;
        if (n.transactionIDs) n.transactionIDs.forEach(id => agg.transactionIDs.add(id));
        mergeMap.set(n.id, agg);
      });
      agg.transactionValue = agg.value;
      aggregated.push(agg);
    });
    if (mergeMap.size) {
      nodes = nodes.filter(n => !mergeMap.has(n.id)).concat(aggregated);
      const relinked = new Map();
      links.forEach(l => {
        const s = mergeMap.get(l.source.id) || l.source;
        const t = mergeMap.get(l.target.id) || l.target;
        if (s === t) return;
        const key = `${s.id}|${t.id}`;
        let m = relinked.get(key);
        if (!m) { m = { source: s, target: t, value: 0, transactionIDs: new Set() }; relinked.set(key, m); }
        m.value += l.value;
        if (l.transactionIDs) l.transactionIDs.forEach(id => m.transactionIDs.add(id));
      });
      links = [...relinked.values()];
    }
  }

  // ---- columns (compacted: skip any that ended up empty) ----
  const cols = [...new Set(nodes.map(n => n.col))].sort((a, b) => a - b);
  const colCount = cols.length;
  const colIndex = new Map(cols.map((c, i) => [c, i]));
  const innerW = Math.max(1, width - margin.left - margin.right - NODE_W);
  const innerH = Math.max(1, height - margin.top - margin.bottom);
  const xOf = (col) => (colCount <= 1
    ? margin.left + innerW / 2
    : margin.left + colIndex.get(col) * (innerW / (colCount - 1)));
  const columnsArr = cols.map(c => nodes.filter(n => n.col === c));

  // Height budget for the busiest column: gaps may use at most half the
  // height and minimum node heights at most a quarter, so at least a quarter
  // is always left for values. With a fixed gap and a fixed 1px minimum, a
  // crowded column ("0 = all" nodes, hundreds of merchants) used up the whole
  // height; the value scale went to zero or below, and the old `scale = 1`
  // fallback drew one pixel per dollar — a single giant bar with every label
  // off-chart.
  const MAX_GAP_SHARE = 0.5;
  const MAX_MIN_HEIGHT_SHARE = 0.25;
  const maxColLen = Math.max(1, ...columnsArr.map(c => c.length));
  const PAD = maxColLen > 1
    ? Math.min(PAD_REQUESTED, (innerH * MAX_GAP_SHARE) / (maxColLen - 1))
    : PAD_REQUESTED;
  const MIN_NODE_H = Math.min(1, (innerH * MAX_MIN_HEIGHT_SHARE) / maxColLen);

  // vertical scale so the busiest column fits (avail >= innerH/4 by the caps)
  let scale = Infinity;
  columnsArr.forEach(colNodes => {
    const tot = colNodes.reduce((s, n) => s + (n.value || 0), 0);
    const avail = innerH - Math.max(0, colNodes.length - 1) * PAD - colNodes.length * MIN_NODE_H;
    if (tot > 0) scale = Math.min(scale, avail / tot);
  });
  // No positive totals anywhere: nodes fall back to their minimum height.
  if (!isFinite(scale) || scale <= 0) scale = 0;

  // initial vertical stack, centered per column
  columnsArr.forEach((colNodes, ci) => {
    colNodes.sort((a, b) => (b.value || 0) - (a.value || 0));
    const totalH = colNodes.reduce((s, n) => s + Math.max(MIN_NODE_H, (n.value || 0) * scale), 0)
      + Math.max(0, colNodes.length - 1) * PAD;
    let y = margin.top + Math.max(0, (innerH - totalH) / 2);
    const x = xOf(cols[ci]);
    colNodes.forEach(n => {
      const h = Math.max(MIN_NODE_H, (n.value || 0) * scale);
      n.x0 = x; n.x1 = x + NODE_W; n.y0 = y; n.y1 = y + h;
      y = n.y1 + PAD;
    });
  });

  // relaxation to align nodes with their connections + resolve overlaps
  const top = margin.top, bottom = margin.top + innerH;
  for (let it = 0; it < 6; it++) {
    const forward = it % 2 === 0;
    columnsArr.forEach(colNodes => {
      colNodes.forEach(n => {
        const rel = links.filter(l => (forward ? l.target.id : l.source.id) === n.id);
        if (rel.length) {
          const w = rel.reduce((s, l) => s + Math.abs(l.value), 0) || 1;
          const center = rel.reduce((s, l) => {
            const o = forward ? l.source : l.target;
            return s + ((o.y0 + o.y1) / 2) * Math.abs(l.value);
          }, 0) / w;
          // Defense in depth: a non-finite center (from any bad link/node value
          // that slipped past the builder) would spread NaN node-to-node and
          // blank the whole chart. Skip the alignment nudge instead of
          // corrupting this node's geometry.
          if (Number.isFinite(center)) {
            const h = n.y1 - n.y0;
            const c = (n.y0 + n.y1) / 2 + 0.5 * (center - (n.y0 + n.y1) / 2);
            n.y0 = c - h / 2; n.y1 = c + h / 2;
          }
        }
      });
      colNodes.sort((a, b) => a.y0 - b.y0);
      for (let i = 1; i < colNodes.length; i++) {
        const min = colNodes[i - 1].y1 + PAD;
        if (colNodes[i].y0 < min) { const d = min - colNodes[i].y0; colNodes[i].y0 += d; colNodes[i].y1 += d; }
      }
      for (let i = colNodes.length - 1; i >= 0; i--) {
        if (colNodes[i].y1 > bottom) { const d = colNodes[i].y1 - bottom; colNodes[i].y0 -= d; colNodes[i].y1 -= d; }
        if (i < colNodes.length - 1) {
          const max = colNodes[i + 1].y0 - PAD;
          if (colNodes[i].y1 > max) { const d = colNodes[i].y1 - max; colNodes[i].y0 -= d; colNodes[i].y1 -= d; }
        }
      }
      if (colNodes.length && colNodes[0].y0 < top) {
        const d = top - colNodes[0].y0; colNodes.forEach(n => { n.y0 += d; n.y1 += d; });
      }
    });
  }

  // link geometry: width + stacked source/target endpoints (d3 sankeyLinkHorizontal)
  // True widths (no 1px floor) so a node's stacked ribbons always fit inside
  // it — hundreds of 1px-floored ribbons overflowed their node. The renderer
  // still strokes each ribbon at least 1px wide so hairlines stay visible.
  links.forEach(l => {
    const w = Math.abs(l.value) * scale;
    l.width = Number.isFinite(w) ? w : 1; // 1px placeholder for a corrupt value
  });
  nodes.forEach(n => {
    const out = links.filter(l => l.source.id === n.id)
      .sort((a, b) => (a.target.y0 + a.target.y1) / 2 - (b.target.y0 + b.target.y1) / 2);
    const inc = links.filter(l => l.target.id === n.id)
      .sort((a, b) => (a.source.y0 + a.source.y1) / 2 - (b.source.y0 + b.source.y1) / 2);
    let yo = n.y0; out.forEach(l => { l.y0 = yo + l.width / 2; yo += l.width; });
    let yi = n.y0; inc.forEach(l => { l.y1 = yi + l.width / 2; yi += l.width; });
  });

  return { nodes, links };
}